/**
 * Error Boundary for Canvas Nodes
 *
 * Catches React errors in node components and displays a graceful
 * error state instead of crashing the entire canvas.
 * Includes a retry button that resets error state (BUILD_ORDER Q3).
 */

import { Component, ErrorInfo, ReactNode } from 'react'
import { Translation } from 'react-i18next'
import { createDebug } from '../../utils/debugLog'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'
import { readErrorName } from '../../analytics/errorClass'
import { siteFromError } from '../../analytics/errorSite'
import { trackCanvasNodeRenderFailed } from '../../analytics/events'
import { NODE_CARD_RADIUS_CLASS } from './nodes/shared/constants'

// Forwarded to backend nad-debug.log via /api/debug-log so render crashes
// land in the same timeline as backend events.
const errLog = createDebug('dw:canvas:node-error')

interface Props {
  children: ReactNode
  nodeId?: string
  /** Human-readable label used for the visible error header (e.g. "Image Generator"). */
  nodeType?: string
  /** STABLE registry key for analytics (e.g. "imageGenerator"), NOT the label. */
  nodeTypeKey?: string
  /** Project the node belongs to (from ProjectIdContext) — required to emit analytics. */
  projectId?: string
}

interface State {
  hasError: boolean
  error: Error | null
}

export class NodeErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const payload = {
      nodeId: this.props.nodeId,
      nodeType: this.props.nodeType,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    }
    console.error('[NodeErrorBoundary] Caught error in node:', payload)
    // Forward to backend nad-debug.log so the failure is visible alongside
    // backend events. NAD log payloads are structured for grep / trace tooling.
    errLog(payload)
    // App-health analytics (RFC-0974 §5.3): coarse node-UI-crash signal — stable
    // node_type key + raw error class name + bundle site (NO message/stack). project_id
    // is required; if absent (boundary outside a project) we skip rather than
    // send an empty identity. Best-effort, never re-raise.
    try {
      const { nodeTypeKey, projectId, nodeId } = this.props
      if (projectId) {
        trackCanvasNodeRenderFailed({
          node_type: nodeTypeKey ?? 'unknown',
          error_name: readErrorName(error),
          error_site: siteFromError(error),
          project_id: projectId,
          node_id: nodeId,
        })
      } else {
        errLog({ event: 'node-render-failed-skip-no-project', nodeTypeKey, nodeId })
      }
    } catch {
      /* analytics must never break the error boundary */
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      const { nodeType, nodeId } = this.props
      const errorMessage = this.state.error?.message

      return (
        <Translation ns="canvas">
          {(t) => (
            <div className={`${NODE_CARD_RADIUS_CLASS} shadow bg-card border border-red-500/50 min-w-[200px]`}>
              {/* Header */}
              <div className="px-3 py-2 bg-red-500/20 border-b border-red-500/30 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-red-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <span className="text-xs font-medium text-red-300">
                    {nodeType
                      ? t('nodeErrorBoundary.nodeError', { nodeType })
                      : t('nodeErrorBoundary.nodeErrorDefault')}
                  </span>
                </div>
                <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
              </div>

              {/* Error Content */}
              <div className="p-3">
                <p className="text-xxs text-red-300/80 font-mono break-all">
                  {errorMessage || t('nodeErrorBoundary.unknownError')}
                </p>
                {nodeId && (
                  <p className="text-xxs text-foreground/30 mt-2 font-mono">
                    ID: {nodeId}
                  </p>
                )}
              </div>

              {/* Footer with retry button */}
              <div className="px-3 py-2 bg-canvas border-t border-white/5 rounded-b-lg flex items-center justify-between">
                <p className="text-xxs text-text-muted">
                  {t('nodeErrorBoundary.checkConsole')}
                </p>
                <button
                  onClick={this.handleRetry}
                  className="text-xxs px-2 py-0.5 rounded border border-red-500/40 text-red-300 hover:bg-red-500/20 transition-colors cursor-pointer"
                >
                  {t('nodeErrorBoundary.retry')}
                </button>
              </div>
            </div>
          )}
        </Translation>
      )
    }

    return this.props.children
  }
}

/**
 * HOC to wrap a node component with error boundary.
 * Passes props.id as nodeId, the human `nodeType` label (for the visible header),
 * and the stable `nodeTypeKey` registry key (for analytics). Reads the current
 * project id from context so a node crash carries its owning project.
 */
export function withNodeErrorBoundary<P>(
  WrappedComponent: React.ComponentType<P>,
  nodeType: string,
  nodeTypeKey?: string
) {
  function WithErrorBoundary(props: P & { id?: string }) {
    const projectId = useOptionalProjectId()
    return (
      <NodeErrorBoundary
        nodeId={props.id}
        nodeType={nodeType}
        nodeTypeKey={nodeTypeKey}
        projectId={projectId}
      >
        <WrappedComponent {...props} />
      </NodeErrorBoundary>
    )
  }
  WithErrorBoundary.displayName = `WithErrorBoundary(${nodeTypeKey ?? nodeType})`
  return WithErrorBoundary
}
